// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Explorer panel annunciations: derives the alert items at a given
// graph cursor point (finding-window alerts + hot-CHT + bus-voltage +
// oil temp/pressure), builds the detail HTML for the selected item,
// and renders the chips + detail panel inside #explorerAnnunciations.
//
// Exposed as a factory because there are ~14 hoisted app.js helpers
// to bind plus the mutable explorerSelectedAlertId / healthSummary /
// flightData globals.

import { formatCompactElapsedTime, formatElapsedTime } from "../core/format.js";
import { valveFrequencyLabel } from "../analysis/valve.js";
import { elevatedEgtChannels } from "../findings/metric-findings.js";
import { explorerAlertTone } from "../charts/presentation.js";
import { pointInDetectionWindow } from "../findings/detection-windows.js";
import { findingCausePresentation } from "../findings/presentation.js";
import { cylinderNumberFromKey } from "../core/schema.js";
import { chtParameters } from "../analysis/detectors/cht.js";

export function createExplorerAnnunciations({
  escapeHtml,
  getHealthMetricLabels,
  getHealthSummary,
  getFlightData,
  getExplorerSelectedAlertId,
  setExplorerSelectedAlertId,
  findingForMetric,
  calculateHealthSummary,
  currentEngineDescriptors,
  engineScopedCylinderLabel,
  engineInfoForSourceNumber,
  engineInfoForCylinderNumber,
  availableCylinderNumbers,
  playbackDetectionWindows,
  explorerFindingChip,
  readableProbeLabel,
  buildExplorerTimelineMarkers,
}) {
  function updateExplorerAnnunciationHeader(point) {
    const header = document.querySelector("#explorerAnnunciations .explorer-annunciation-header");
    if (!header) return;
    const time = header.querySelector("strong");
    const phase = header.querySelector("small");
    if (time) {
      const timeText = point ? formatElapsedTime(point.elapsedSeconds) : "--";
      if (time.textContent !== timeText) time.textContent = timeText;
    }
    if (phase) {
      const phaseText = point?.phase ?? "Phase unknown";
      if (phase.textContent !== phaseText) phase.textContent = phaseText;
    }
  }

  function explorerAlertId(...parts) {
    return parts
      .map((part) => String(part ?? "x").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "x")
      .join("-");
  }

  function explorerWindowTimeLabel(window) {
    if (!window || !Number.isFinite(window.startTime)) return "";
    const start = formatCompactElapsedTime(window.startTime);
    const end = Number.isFinite(window.endTime) ? formatCompactElapsedTime(window.endTime) : start;
    return start === end ? start : `${start}-${end}`;
  }

  function explorerScoreText(metric) {
    return Number.isFinite(metric?.score) ? `${Math.round(metric.score)}/100` : "No score";
  }

  function explorerValveEvidenceItems(point, metric, window) {
    const top = metric?.details?.top ?? {};
    const best = top.best ?? {};
    const windowAmplitude = Number.isFinite(window?.amplitude) ? window.amplitude : best.amplitude;
    const windowRatio = Number.isFinite(window?.ratio) ? window.ratio : best.ratio;
    const windowFrequency = window?.frequency ?? best.frequency;
    const windowLabel = explorerWindowTimeLabel(window);
    const cylinderNumber = Number.isFinite(window?.sourceCylinder) ? window.sourceCylinder : top.cylinder;
    const cylinder = Number.isFinite(cylinderNumber)
      ? engineScopedCylinderLabel(cylinderNumber, currentEngineDescriptors())
      : "Strongest cylinder";
    const amplitude = Number.isFinite(windowAmplitude) ? `${Math.round(windowAmplitude)} F peak-to-peak` : "";
    const ratio = Number.isFinite(windowRatio) ? `${windowRatio.toFixed(1)}x peer median` : "";
    const frequency = valveFrequencyLabel(windowFrequency);
    const repeat = Number.isFinite(top.suspiciousWindows)
      ? `${top.suspiciousWindows} flagged stable check${top.suspiciousWindows === 1 ? "" : "s"}`
      : "";
    return [
      { label: "Now", value: formatElapsedTime(point?.elapsedSeconds ?? 0), detail: point?.phase ?? "Phase unknown" },
      windowLabel ? { label: "Window", value: windowLabel, detail: window?.label ?? "" } : null,
      amplitude ? { label: "EGT swing", value: amplitude, detail: `${cylinder}; ${ratio || "peer comparison unavailable"}` } : null,
      frequency !== "No dominant frequency band" ? { label: "Rhythm", value: frequency, detail: repeat || "Dominant frequency in this stable-power window" } : null,
      { label: "Score", value: explorerScoreText(metric), detail: metric?.note ?? "" },
    ].filter(Boolean);
  }

  function explorerValveAlertBody(metric, finding, window) {
    const top = metric?.details?.top ?? {};
    const best = top.best ?? {};
    const cylinderNumber = Number.isFinite(window?.sourceCylinder) ? window.sourceCylinder : top.cylinder;
    const amplitude = Number.isFinite(window?.amplitude) ? window.amplitude : best.amplitude;
    const ratio = Number.isFinite(window?.ratio) ? window.ratio : best.ratio;
    const frequencySource = window?.frequency ?? best.frequency;
    if (!Number.isFinite(cylinderNumber) || !Number.isFinite(amplitude)) {
      return finding?.body ?? "One cylinder's EGT fluctuated more than the others during steady power. It is not enough by itself to suggest a mechanical issue unless it repeats.";
    }
    const frequency = valveFrequencyLabel(frequencySource);
    const frequencyText = frequency !== "No dominant frequency band" ? `, ${frequency}` : "";
    const ratioText = Number.isFinite(ratio) ? `, ${ratio.toFixed(1)}x the peer median` : "";
    const cylinderLabel = engineScopedCylinderLabel(cylinderNumber, currentEngineDescriptors());
    return `${cylinderLabel}'s EGT varied about ${Math.round(amplitude)} F peak-to-peak during steady power${frequencyText}${ratioText}. This is a review cue, not a mechanical conclusion; repeatability and corroborating maintenance evidence matter most.`;
  }

  function explorerAlertBodyForFinding(key, metric, finding, window) {
    if (key === "valve") return explorerValveAlertBody(metric, finding, window);
    return {
      sensors: finding?.body ?? "The flagged probe is not matching peer-cylinder behavior in this window. Treat related engine findings as lower confidence until the probe, wiring, or logged channel is checked.",
      cht: "CHT crossed the review threshold on the warmest cylinder. Compare it with phase, power, and the other cylinders.",
      egt: "EGT spread is wider than expected. One or more EGT channels are separating from the peer-cylinder pattern.",
      combustion: "Cylinder-head temperature and exhaust temperature changed together in this window. Look for a practical explanation first: a mag check, mixture or fuel-flow change, power change, injector/fuel distribution issue, or a probe/sensor problem.",
      divergence: "Several cylinder temperature traces moved away from the rest, then started coming back together.",
      descentEgtDropout: "One EGT trace cooled faster than the others during a low-power descent window. This is not automatically a fault; compare fuel flow, MAP, RPM, altitude, and repeatability.",
      inductionLeak: "RPM and fuel flow decreased while EGT rose. If MAP is logged, a simultaneous MAP decrease strengthens the induction-leak cue.",
      mag: "A likely mag-check segment has an uneven or inconclusive EGT response. Confirm pilot or switch context before treating it as ignition evidence.",
      carbIce: "Carb temperature, OAT, and whole-engine power movement line up with a possible carb-ice cue. Confirm carb-heat use, weather, and pilot notes before treating it as the cause.",
      cooling: "One cylinder is hotter than its peers without a matching EGT pattern. If it repeats, this points toward cooling airflow or baffling context.",
      coolingRate: "CHT dropped faster than the cooling-rate trigger in this window. Confirm percent power, RPM/MAP, airspeed, and probe behavior around the descent.",
      charging: "Post-start bus-voltage recovery is weaker than expected. Check whether the alternator or current trace comes online after engine start.",
      electrical: "Bus voltage is outside the expected running range at this point. Compare it with RPM, phase, and electrical load.",
    }[key] ?? finding?.body ?? metric?.note ?? "This annunciation is tied to a marked review window in the graph.";
  }

  function explorerEgtAnnunciationLabel(metric) {
    const hot = metric?.details?.hot;
    if (!hot?.isReview) return "";
    const elevated = elevatedEgtChannels(hot);
    if (elevated.length > 1) {
      const descriptors = currentEngineDescriptors();
      const engines = elevated
        .map((channel) => engineInfoForSourceNumber(cylinderNumberFromKey(channel.key), descriptors).engine)
        .filter(Boolean);
      const unique = new Map(engines.map((engine) => [engine.id, engine]));
      if (unique.size === 1) {
        const engine = [...unique.values()][0];
        return engine.side === "single" ? "EGT HIGH" : `${engine.shortLabel} EGT HIGH`;
      }
      return "EGT HIGH";
    }
    const probe = readableProbeLabel(hot.key) || "EGT";
    return `${probe} HIGH`;
  }

  function explorerFindingAnnunciationItem(point, window, label) {
    const healthSummary = getHealthSummary();
    const key = window?.sourceKey ?? window?.key ?? "finding";
    const metric = healthSummary?.[key];
    const check = key === "mag" ? metric?.details?.events?.find((event) => event.startTime === window.startTime && event.endTime === window.endTime) : null;
    const findingSummary = check ? { ...healthSummary, mag: { ...metric, details: { ...metric.details, top: check } } } : healthSummary;
    const finding = findingForMetric(key, findingSummary ?? calculateHealthSummary(getFlightData()));
    const windowLabel = explorerWindowTimeLabel(window);
    const phase = point?.phase ?? "Phase unknown";
    const signal = finding?.title ?? label;
    return {
      id: explorerAlertId("explorer", key, window?.startTime, window?.endTime, label),
      key,
      tone: explorerAlertTone(key, metric?.score),
      label,
      kicker: window?.sourceLabel ?? getHealthMetricLabels()[key] ?? "Engine health",
      title: finding?.title ?? `${getHealthMetricLabels()[key] ?? "Engine"} review`,
      meta: [windowLabel, phase, explorerScoreText(metric)].filter(Boolean).join(" | "),
      body: explorerAlertBodyForFinding(key, metric, finding, window),
      evidence: [
        ...(key === "valve" ? explorerValveEvidenceItems(point, metric, window) : [
          { label: "Now", value: formatElapsedTime(point?.elapsedSeconds ?? 0), detail: phase },
          windowLabel ? { label: "Window", value: windowLabel, detail: window?.label ?? "" } : null,
          ...(key === "sensors" ? (finding?.evidence ?? []).slice(0, 2) : []),
          { label: "Check", value: signal, detail: metric?.note ?? "" },
          { label: "Score", value: explorerScoreText(metric), detail: metric?.note ?? "" },
        ]),
      ].filter(Boolean),
      causes: finding?.causes ?? [],
      causePresentation: findingCausePresentation(finding, metric),
      actions: finding?.actions ?? [],
      jumpTime: window?.startTime,
    };
  }

  function explorerHottestCylinder(point) {
    let top = null;
    availableCylinderNumbers([point]).forEach((number) => {
      const cht = point?.[`cht${number}`];
      if (!Number.isFinite(cht)) return;
      if (!top || cht > top.cht) top = { number, cht };
    });
    return top;
  }

  function explorerHotChtCylinders(point, threshold = chtParameters.cautionThresholdF) {
    return availableCylinderNumbers([point])
      .map((number) => ({ number, cht: point?.[`cht${number}`] }))
      .filter((item) => Number.isFinite(item.cht) && item.cht >= threshold)
      .sort((a, b) => a.number - b.number);
  }

  function explorerHotChtLabelForCylinder(hot, engines) {
    const { engine, cylinder } = engineInfoForCylinderNumber(hot.number, engines);
    const displayNumber = cylinder?.number ?? hot.number;
    const engineLabel = String(engine?.label ?? engine?.shortLabel ?? "Engine").replace(/\s+Engine$/i, "");
    const label = engine && engines.length > 1
      ? `${engineLabel} CHT ${displayNumber}`
      : `CHT ${displayNumber}`;
    return { label, displayNumber };
  }

  function explorerHotChtAnnunciationItem(point, hot) {
    if (!hot || !Number.isFinite(hot.cht)) return null;
    const healthSummary = getHealthSummary();
    const metric = healthSummary?.cht;
    const finding = findingForMetric("cht", healthSummary ?? calculateHealthSummary(getFlightData()));
    const engines = currentEngineDescriptors([point]);
    const { label: cylinderLabel, displayNumber } = explorerHotChtLabelForCylinder(hot, engines);
    return {
      id: explorerAlertId("explorer", "cht-hot", hot.number),
      key: "cht",
      tone: hot.cht >= 450 ? "danger" : "warning",
      label: `CHT ${displayNumber} HOT`,
      kicker: "Cylinder head temperature",
      title: `${cylinderLabel} is ${Math.round(hot.cht)} F`,
      meta: [point?.phase, explorerScoreText(metric)].filter(Boolean).join(" | "),
      body: `${cylinderLabel} is above the ${Math.round(chtParameters.cautionThresholdF)} F playback annunciation threshold at the graph cursor. Compare it with the other cylinders and phase before treating it as sustained heat.`,
      evidence: [
        { label: "Now", value: formatElapsedTime(point?.elapsedSeconds ?? 0), detail: point?.phase ?? "" },
        { label: cylinderLabel, value: `${Math.round(hot.cht)} F`, detail: `Playback annunciation threshold ${Math.round(chtParameters.cautionThresholdF)} F` },
        { label: "Score", value: explorerScoreText(metric), detail: metric?.note ?? "" },
      ],
      causes: finding?.causes ?? [
        ["High-power or low-airspeed operating context", 0.34],
        ["Cooling-airflow or baffle issue if repeatable", 0.27],
        ["CHT probe or installation context", 0.13],
      ],
      causePresentation: findingCausePresentation(finding, metric),
      actions: finding?.actions ?? [],
      jumpTime: point?.elapsedSeconds,
    };
  }

  function explorerBusTone(point) {
    const value = point?.buss;
    if (!Number.isFinite(value)) return "";
    const details = getHealthSummary()?.electrical?.details ?? {};
    const isTwentyEightVolt = value > 18 || details.highLimit > 18;
    const low = details.lowLimit ?? (isTwentyEightVolt ? 24 : 12.2);
    const high = details.highLimit ?? (isTwentyEightVolt ? 29.6 : 14.8);
    if (value < low || value > high) return Math.abs(value - (value < low ? low : high)) >= 1 ? "danger" : "warning";
    return "";
  }

  function explorerBusAnnunciationItem(point) {
    const healthSummary = getHealthSummary();
    const metric = healthSummary?.electrical;
    const finding = findingForMetric("electrical", healthSummary ?? calculateHealthSummary(getFlightData()));
    const details = metric?.details ?? {};
    const isTwentyEightVolt = (point?.buss ?? 0) > 18 || details.highLimit > 18;
    const low = details.lowLimit ?? (isTwentyEightVolt ? 24 : 12.2);
    const high = details.highLimit ?? (isTwentyEightVolt ? 29.6 : 14.8);
    const lowAlert = point.buss < low;
    return {
      id: explorerAlertId("explorer", "bus", lowAlert ? "low" : "high"),
      key: "electrical",
      tone: explorerBusTone(point),
      label: lowAlert ? "BUS LOW" : "BUS HIGH",
      kicker: "Electrical",
      title: `Bus voltage flagged ${lowAlert ? "low" : "high"}`,
      meta: [point?.phase, `Expected ${low.toFixed(1)}-${high.toFixed(1)} V`].filter(Boolean).join(" | "),
      body: `Bus voltage is ${point.buss.toFixed(1)} V at the cursor. Compare it with RPM and electrical load around this point.`,
      evidence: [
        { label: "Now", value: formatElapsedTime(point?.elapsedSeconds ?? 0), detail: point?.phase ?? "" },
        { label: "Bus", value: `${point.buss.toFixed(1)} V`, detail: `Expected ${low.toFixed(1)}-${high.toFixed(1)} V` },
        { label: "Score", value: explorerScoreText(metric), detail: metric?.note ?? "" },
      ],
      causes: finding?.causes ?? [
        ["Alternator/regulator behavior if repeated", 0.32],
        ["Electrical load or intermittent load change", 0.24],
        ["Voltage sensor/reporting issue", 0.17],
      ],
      causePresentation: findingCausePresentation(finding, metric),
      actions: finding?.actions ?? [],
      jumpTime: point?.elapsedSeconds,
    };
  }

  function explorerOilAnnunciationItem(point, key, label, unit, low, high) {
    const value = point?.[key];
    const lowAlert = value < low;
    return {
      id: explorerAlertId("explorer", key, lowAlert ? "low" : "high"),
      key,
      tone: "warning",
      label,
      kicker: "Oil system",
      title: `${label.replace("OIL ", "Oil ")} flagged ${lowAlert ? "low" : "high"}`,
      meta: `Expected ${low}-${high}${unit}`,
      body: `${label.replace("OIL ", "Oil ")} is ${Number(value).toFixed(0)}${unit} at the cursor. Check whether the excursion persists after start or warmup before treating it as sustained oil-system evidence.`,
      evidence: [
        { label: "Now", value: formatElapsedTime(point?.elapsedSeconds ?? 0), detail: point?.phase ?? "" },
        { label, value: `${Number(value).toFixed(0)}${unit}`, detail: `Threshold ${low}-${high}${unit}` },
      ],
      causes: lowAlert ? [
        ["Warmup, idle, or sensor range context", 0.34],
        ["Oil quantity, viscosity, or temperature context if persistent", 0.26],
        ["Sensor or sender behavior if the trace is abrupt", 0.18],
      ] : [
        ["High operating temperature or low cooling margin", 0.34],
        ["Oil cooler, vernatherm, airflow, or pressure-relief context if persistent", 0.26],
        ["Sensor or sender behavior if the trace is abrupt", 0.18],
      ],
      causePresentation: { title: "Possible Causes", weighted: false },
      actions: [
        ["medium", "Compare this point with the nearby oil trend and phase"],
        ["low", "Check whether the same excursion repeats in similar operation"],
      ],
      jumpTime: point?.elapsedSeconds,
    };
  }

  function explorerAnnunciationWindowPriority(window) {
    const key = window?.sourceKey ?? window?.key;
    if (key === "divergence") return 0;
    if (key === "combustion" || key === "valve" || key === "mag") return 1;
    if (window?.kind === "condition" || key === "sensors") return 2;
    return 1.5;
  }

  function explorerAnnunciationItems(point) {
    if (!point) return [{ id: "no-data", label: "NO DATA", disabled: true }];
    const items = playbackDetectionWindows()
      .filter((window) => pointInDetectionWindow(point, window))
      .sort((a, b) => explorerAnnunciationWindowPriority(a) - explorerAnnunciationWindowPriority(b) || a.startTime - b.startTime)
      .map((window) => {
        const label = explorerFindingChip(window);
        return label ? explorerFindingAnnunciationItem(point, window, label) : null;
      })
      .filter(Boolean)
      .slice(0, 3);

    explorerHotChtCylinders(point).forEach((hot) => {
      const hotItem = explorerHotChtAnnunciationItem(point, hot);
      if (hotItem) items.push(hotItem);
    });
    if (explorerBusTone(point)) items.push(explorerBusAnnunciationItem(point));
    if (Number.isFinite(point.oilTemp) && (point.oilTemp < 60 || point.oilTemp > 240)) {
      items.push(explorerOilAnnunciationItem(point, "oilTemp", "OIL TEMP", " F", 60, 240));
    }
    if (Number.isFinite(point.oilPressure) && (point.oilPressure < 20 || point.oilPressure > 100)) {
      items.push(explorerOilAnnunciationItem(point, "oilPressure", "OIL PRESS", " psi", 20, 100));
    }
    return items.length ? items : [{ id: "clear", label: "No active annunciations", disabled: true }];
  }

  function syncExplorerSelectedAnnunciation(items) {
    const active = items.filter((item) => !item.disabled);
    if (!active.length) {
      setExplorerSelectedAlertId(null);
      return null;
    }
    const currentId = getExplorerSelectedAlertId();
    if (!currentId) return null;
    const selected = active.find((item) => item.id === currentId) ?? null;
    if (!selected) setExplorerSelectedAlertId(null);
    return selected;
  }

  function explorerAlertEvidenceHtml(evidence = []) {
    const items = evidence
      .filter((item) => item?.label && (item.value || item.detail))
      .slice(0, 5);
    if (!items.length) return "";
    return `
      <div class="playback-alert-evidence" aria-label="Why flagged">
        <span class="playback-alert-evidence-title">Why flagged</span>
        <ul>
          ${items.map((item) => `
            <li${item.detail ? ` title="${escapeHtml(item.detail)}"` : ""}>
              <b>${escapeHtml(item.label)}</b>
              <span>${escapeHtml(item.value ?? item.detail)}</span>
            </li>
          `).join("")}
        </ul>
      </div>
    `;
  }

  function explorerAlertActionsHtml(actions = []) {
    const items = actions.slice(0, 3);
    if (!items.length) return "";
    return `
      <div class="playback-alert-list">
        <span>Review next</span>
        <ol>
          ${items.map(([priority, action]) => `<li><b class="is-${escapeHtml(priority)}">${escapeHtml(String(priority ?? "").toUpperCase())}</b><span>${escapeHtml(action)}</span></li>`).join("")}
        </ol>
      </div>
    `;
  }

  function explorerAlertCausesHtml(causes = [], presentation = {}) {
    const items = causes.slice(0, 3);
    if (!items.length) return "";
    const weighted = presentation?.weighted === true;
    return `
      <div class="playback-alert-list">
        <span>${escapeHtml(presentation?.title ?? "Possible causes")}</span>
        <ol>
          ${items.map(([cause, probability], index) => `<li><b>${weighted ? `${Math.round((probability ?? 0) * 100)}%` : index + 1}</b><span>${escapeHtml(cause)}</span></li>`).join("")}
        </ol>
      </div>
    `;
  }

  function explorerAnnunciationDetailHtml(item) {
    if (!item) return "";
    const meta = item.meta ? `<small>${escapeHtml(item.meta)}</small>` : "";
    return `
      <article class="explorer-annunciation-detail is-${item.tone ?? "warning"}" id="explorerAnnunciationDetail" aria-label="Annunciation detail">
        <header>
          <div>
            <p>${escapeHtml(item.kicker ?? "Annunciation")}</p>
            <h3>${escapeHtml(item.title)}</h3>
            ${meta}
          </div>
          <button class="playback-alert-close" data-explorer-alert-close type="button" aria-label="Close annunciation detail"></button>
        </header>
        <p>${escapeHtml(item.body)}</p>
        ${explorerAlertEvidenceHtml(item.evidence)}
        <div class="playback-alert-columns">
          ${explorerAlertCausesHtml(item.causes, item.causePresentation)}
          ${explorerAlertActionsHtml(item.actions)}
        </div>
        <footer>
          <span>Advisory only</span>
        </footer>
      </article>
    `;
  }

  function renderExplorerAnnunciations(data, point, markers = buildExplorerTimelineMarkers(data)) {
    const container = document.querySelector("#explorerAnnunciations");
    if (!container) return;
    if (!data.length) {
      container.innerHTML = '<p class="explorer-annunciation-empty">Load graph data to see annunciations.</p>';
      return;
    }
    const items = explorerAnnunciationItems(point);
    const selected = syncExplorerSelectedAnnunciation(items);
    const currentSelectedId = getExplorerSelectedAlertId();
    const chips = items.map((item) => {
      if (item.disabled) return `<span class="explorer-annunciation-empty-chip">${escapeHtml(item.label)}</span>`;
      const selectedClass = item.id === currentSelectedId ? " is-selected" : "";
      const toneClass = item.tone ? ` is-${item.tone}` : "";
      const expanded = item.id === currentSelectedId ? "true" : "false";
      return `<button class="explorer-annunciation-chip${toneClass}${selectedClass}" data-explorer-alert-id="${escapeHtml(item.id)}" aria-expanded="${expanded}" aria-controls="explorerAnnunciationDetail" type="button">${escapeHtml(item.label)}</button>`;
    }).join("");
    const time = point ? formatElapsedTime(point.elapsedSeconds) : "--";
    container.innerHTML = `
      <header class="explorer-annunciation-header">
        <span>Annunciations</span>
        <strong>${escapeHtml(time)}</strong>
        <small>${escapeHtml(point?.phase ?? "Phase unknown")}</small>
      </header>
      <div class="explorer-annunciation-chips">${chips}</div>
      ${explorerAnnunciationDetailHtml(selected)}
    `;
  }

  return {
    updateExplorerAnnunciationHeader,
    explorerAlertId,
    explorerWindowTimeLabel,
    explorerScoreText,
    explorerValveEvidenceItems,
    explorerValveAlertBody,
    explorerAlertBodyForFinding,
    explorerEgtAnnunciationLabel,
    explorerFindingAnnunciationItem,
    explorerHottestCylinder,
    explorerHotChtCylinders,
    explorerHotChtLabelForCylinder,
    explorerHotChtAnnunciationItem,
    explorerBusTone,
    explorerBusAnnunciationItem,
    explorerOilAnnunciationItem,
    explorerAnnunciationWindowPriority,
    explorerAnnunciationItems,
    syncExplorerSelectedAnnunciation,
    explorerAlertEvidenceHtml,
    explorerAlertActionsHtml,
    explorerAlertCausesHtml,
    explorerAnnunciationDetailHtml,
    renderExplorerAnnunciations,
  };
}
